import { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { UserProfile } from '../../types'
import apiService from '../../services/api'

// Authentication response types based on the design document
interface AuthTokens {
  accessToken: string
  refreshToken: string
  idToken: string
  expiresIn: number // Seconds until expiration
}

interface LoginResponse {
  challengeName?: 'NEW_PASSWORD_REQUIRED'
  session?: string // For password change challenge
  tokens?: AuthTokens
  user?: UserProfile
}

interface StoredSession {
  accessToken: string
  refreshToken: string
  idToken: string
  expiresAt: number // Unix timestamp
  user: UserProfile
}

interface AuthContextType {
  user: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password?: string) => Promise<LoginResponse>
  changePassword: (email: string, session: string, newPassword: string) => Promise<LoginResponse>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  sendLoginCredentials: (email: string) => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<boolean>
  refreshUserProfile: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Token storage keys
  const STORAGE_KEYS = {
    SESSION: 'fwo_auth_session',
    TOKENS: 'fwo_auth_tokens'
  }

  // Check if tokens are expired (with 5 minute buffer)
  const isTokenExpired = (expiresAt: number): boolean => {
    const currentTime = Math.floor(Date.now() / 1000)
    return expiresAt <= (currentTime + 300) // 5 minute buffer
  }

  // Store authentication session in local storage
  const storeSession = (tokens: AuthTokens, user: UserProfile): void => {
    // Only access localStorage in browser environment
    if (typeof window === 'undefined') return
    
    const expiresAt = Math.floor(Date.now() / 1000) + tokens.expiresIn
    
    const session: StoredSession = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      idToken: tokens.idToken,
      expiresAt,
      user
    }

    console.log('[AuthProvider] Storing session:', { user: user.email, expiresAt })
    localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(session))
    
    // Set the ID token in API service for API Gateway authorization (not access token)
    apiService.setAuthToken(tokens.idToken)
    console.log('[AuthProvider] ID token set in API service:', tokens.idToken.substring(0, 20) + '...')
    console.log('[AuthProvider] Access token (NOT used for API):', tokens.accessToken.substring(0, 20) + '...')
    console.log('[AuthProvider] Token comparison - ID starts with:', tokens.idToken.substring(0, 30))
    console.log('[AuthProvider] Token comparison - Access starts with:', tokens.accessToken.substring(0, 30))
    
    setUser(user)
    console.log('[AuthProvider] Session stored, user state updated')
  }

  // Retrieve stored session from local storage
  const getStoredSession = (): StoredSession | null => {
    // Only access localStorage in browser environment
    if (typeof window === 'undefined') return null
    
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SESSION)
      if (!stored) return null
      
      return JSON.parse(stored) as StoredSession
    } catch (error) {
      console.error('Error parsing stored session:', error)
      return null
    }
  }

  // Clear stored session
  const clearSession = (): void => {
    // Only access localStorage in browser environment
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.SESSION)
      localStorage.removeItem(STORAGE_KEYS.TOKENS)
    }
    apiService.setAuthToken(null)
    setUser(null)
  }

  // Refresh access token using refresh token
  const refreshAccessToken = async (refreshToken: string): Promise<AuthTokens | null> => {
    try {
      const response = await apiService.refreshToken()

      if (response.data) {
        // Convert the API response to AuthTokens format
        return {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken || refreshToken, // Use new refresh token if provided, otherwise keep existing
          idToken: response.data.idToken || response.data.accessToken, // Use ID token if available, fallback to access token
          expiresIn: response.data.expiresIn
        }
      }
      
      return null
    } catch (error) {
      console.error('Token refresh failed:', error)
      return null
    }
  }

  // Initialize authentication state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[AuthProvider] Initializing authentication state...')
      const storedSession = getStoredSession()
      
      if (!storedSession) {
        console.log('[AuthProvider] No stored session found')
        setIsLoading(false)
        return
      }

      console.log('[AuthProvider] Found stored session, checking token validity...')
      
      // Check if access token is expired
      if (isTokenExpired(storedSession.expiresAt)) {
        console.log('[AuthProvider] Token expired, attempting refresh...')
        // Try to refresh the token
        const newTokens = await refreshAccessToken(storedSession.refreshToken)
        
        if (newTokens) {
          console.log('[AuthProvider] Token refresh successful')
          // Update stored session with new tokens
          storeSession(newTokens, storedSession.user)
        } else {
          console.log('[AuthProvider] Token refresh failed, clearing session')
          // Refresh failed, clear session
          clearSession()
        }
      } else {
        console.log('[AuthProvider] Token still valid, restoring session')
        // Token is still valid, restore session - use ID token for API Gateway
        apiService.setAuthToken(storedSession.idToken)
        setUser(storedSession.user)
      }

      setIsLoading(false)
    }

    // Listen for session expired events from API service
    const handleSessionExpired = () => {
      console.log('Session expired event received, clearing session and redirecting to login')
      clearSession()
      
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }

    // Add event listener for session expiration (only in browser)
    if (typeof window !== 'undefined') {
      window.addEventListener('auth:session-expired', handleSessionExpired)
    }

    initializeAuth()

    // Cleanup event listener on unmount
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('auth:session-expired', handleSessionExpired)
      }
    }
  }, [])

  // Send login credentials via email
  const sendLoginCredentials = async (email: string): Promise<void> => {
    const response = await apiService.sendLoginCredentials(email)
    
    if (response.error) {
      throw new Error(response.error.message)
    }
  }

  // Login with email and optional password
  const login = async (email: string, password?: string): Promise<LoginResponse> => {
    setIsLoading(true)
    
    try {
      console.log('[AuthProvider] Calling apiService.login...')
      const response = await apiService.login(email, password || '')
      console.log('[AuthProvider] API response:', response)
      
      if (response.error) {
        console.error('[AuthProvider] API returned error:', response.error)
        throw new Error(response.error.message)
      }

      // The API wraps the response in { data: {...}, requestId, timestamp }
      // So we need to access response.data.data to get the actual login data
      const loginData = (response.data as any)?.data || response.data as any
      console.log('[AuthProvider] Login data:', loginData)

      // Handle NEW_PASSWORD_REQUIRED challenge
      if (loginData && loginData.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('[AuthProvider] Returning NEW_PASSWORD_REQUIRED challenge')
        return {
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: loginData.session,
          user: loginData.user
        }
      }

      // Handle successful authentication with tokens
      // Backend returns tokens at top level: accessToken, idToken, refreshToken, expiresIn
      if (loginData && loginData.accessToken && loginData.user) {
        console.log('[AuthProvider] Successful login with tokens')
        const tokens: AuthTokens = {
          accessToken: loginData.accessToken,
          idToken: loginData.idToken,
          refreshToken: loginData.refreshToken,
          expiresIn: loginData.expiresIn
        }
        const user = loginData.user as UserProfile
        storeSession(tokens, user)
        return {
          tokens,
          user
        }
      }

      console.error('[AuthProvider] Invalid response format. loginData:', loginData)
      throw new Error('Invalid login response format')
    } finally {
      setIsLoading(false)
    }
  }

  // Change password (complete NEW_PASSWORD_REQUIRED challenge)
  const changePassword = async (email: string, session: string, newPassword: string): Promise<LoginResponse> => {
    setIsLoading(true)
    
    try {
      const response = await apiService.request('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ email, session, newPassword })
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      // The API wraps the response in { data: {...}, requestId, timestamp }
      const loginData = (response.data as any)?.data || response.data as any

      // Should receive tokens and user data after successful password change
      // Backend returns tokens at top level: accessToken, idToken, refreshToken, expiresIn
      if (loginData && loginData.accessToken && loginData.user) {
        const tokens: AuthTokens = {
          accessToken: loginData.accessToken,
          idToken: loginData.idToken,
          refreshToken: loginData.refreshToken,
          expiresIn: loginData.expiresIn
        }
        const user = loginData.user as UserProfile
        storeSession(tokens, user)
        return {
          tokens,
          user
        }
      }

      throw new Error('Invalid password change response format')
    } finally {
      setIsLoading(false)
    }
  }

  // Logout user
  const logout = async (): Promise<void> => {
    setIsLoading(true)
    
    try {
      // Call logout endpoint to invalidate tokens on server
      await apiService.request('/auth/logout', {
        method: 'POST'
      })
    } catch (error) {
      console.error('Logout API call failed:', error)
      // Continue with local logout even if API call fails
    } finally {
      clearSession()
      setIsLoading(false)
    }
  }

  // Reset password
  const resetPassword = async (email: string): Promise<void> => {
    const response = await apiService.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    })

    if (response.error) {
      throw new Error(response.error.message)
    }
  }

  // Update user profile via backend API
  const updateProfile = async (updates: Partial<UserProfile>): Promise<boolean> => {
    try {
      console.log('[AuthProvider] Profile update requested:', updates)
      
      const response = await apiService.updateUserProfile(updates)
      
      if (response.error) {
        console.error('[AuthProvider] Profile update failed:', response.error)
        return false
      }

      if (response.data) {
        // Backend wraps response in { data: {...}, requestId, timestamp }
        const rawData = response.data as any
        const updatedUser = (rawData.data || rawData) as UserProfile
        setUser(updatedUser)
        
        // Update stored session with fresh data from backend
        const storedSession = getStoredSession()
        if (storedSession && typeof window !== 'undefined') {
          const updatedSession = { ...storedSession, user: updatedUser }
          localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(updatedSession))
        }
        
        return true
      }
      
      return false
    } catch (error) {
      console.error('[AuthProvider] Failed to update profile:', error)
      return false
    }
  }

  // Refresh user profile from backend
  const refreshUserProfile = async (): Promise<boolean> => {
    try {
      console.log('[AuthProvider] Refreshing user profile from backend...')
      const response = await apiService.getUserProfile()
      
      if (response.error) {
        console.error('[AuthProvider] Failed to refresh profile:', response.error)
        return false
      }

      if (response.data) {
        // Backend wraps response in { data: {...}, requestId, timestamp }
        const rawData = response.data as any
        const freshUser = (rawData.data || rawData) as UserProfile
        console.log('[AuthProvider] Profile refreshed successfully:', freshUser)
        setUser(freshUser)
        
        // Update stored session with fresh user data
        const storedSession = getStoredSession()
        if (storedSession && typeof window !== 'undefined') {
          const updatedSession = { ...storedSession, user: freshUser }
          localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(updatedSession))
          console.log('[AuthProvider] Session updated with fresh profile data')
        }
        
        return true
      }
      
      return false
    } catch (error) {
      console.error('[AuthProvider] Failed to refresh profile:', error)
      return false
    }
  }

  const contextValue: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    changePassword,
    logout,
    resetPassword,
    sendLoginCredentials,
    updateProfile,
    refreshUserProfile
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}